// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// Native Web3Auth (WaaS) ownership-signing flow.
//
// Renders inline inside `PreGateShell` when the user picks the Web3Auth
// tile from the chooser. The native analogue of `RecoverySignFlow`: it
// drives a Web3Auth social login in this origin, reconstructs the user's
// secp256k1 key in the browser, signs the V06 ownership challenge once
// with the stateless key-agent, and posts only the DER signature to
// `/prove`. The reconstructed key never leaves the page and the backend
// never sees it — same browser-only boundary as the recovery path.
//
// This replaces the broken cross-origin popup path for Web3Auth users:
// instead of bouncing to `wrap.cobault.io/sign-verifier-challenge` (which
// can't complete a nested Web3Auth login inside the popup), the user
// signs natively here. Web3Auth is reused as a TechCo-provided service so
// the same clientId reconstructs the same key the vault was created with
// — see web3auth-config.ts.
//

import { useState } from 'preact/hooks';
import { api, type ProveResponse } from '../api.js';

type FlowState =
  | { kind: 'idle' }
  | { kind: 'connecting' }
  | { kind: 'signing' }
  | { kind: 'error'; message: string }
  | { kind: 'signed' };

export function Web3AuthSignFlow({
  vaultId,
  derivationIndex,
  onSessionEstablished,
  onCancel,
}: {
  vaultId: string;
  derivationIndex: number;
  onSessionEstablished: (session: ProveResponse) => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<FlowState>({ kind: 'idle' });
  const busy = state.kind === 'connecting' || state.kind === 'signing';

  async function handleSignIn() {
    if (busy) return;
    setState({ kind: 'connecting' });

    // Lazy-load the Web3Auth SDK only when this method is actually used,
    // so non-web3auth users never pay the (large) SDK bundle cost. Vite
    // code-splits this into its own chunk.
    const { web3authConnectAndExtractKey, web3authLogout } = await import(
      '../web3auth-login.js'
    );

    let login: { privateKeyHex: string; email: string | null };
    try {
      login = await web3authConnectAndExtractKey();
    } catch (err) {
      setState({
        kind: 'error',
        message:
          err instanceof Error && /cancel/i.test(err.message)
            ? 'Web3Auth sign-in was cancelled.'
            : 'Web3Auth sign-in failed. Please try again.',
      });
      return;
    }

    // The reconstructed key is now live in this scope only. Sign exactly
    // once, then log out to tear the Web3Auth session down on this origin
    // regardless of outcome — the verifier only ever needs one signature.
    try {
      setState({ kind: 'signing' });

      const challenge = await api.issueChallenge(vaultId);
      if (!challenge) {
        setState({
          kind: 'error',
          message: 'Could not start ownership challenge. Try again in a minute.',
        });
        return;
      }

      const { signOwnershipWithWeb3AuthKey } = await import(
        '@cobault/verifier-key-agent'
      );

      let sigHex: string;
      try {
        sigHex = signOwnershipWithWeb3AuthKey(
          login.privateKeyHex,
          vaultId,
          challenge.nonce_hex,
          derivationIndex,
        );
      } catch (err) {
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Signing failed.',
        });
        return;
      } finally {
        // Drop our reference to the reconstructed key the instant we've
        // signed. JS strings are immutable so we can't zero the bytes, but
        // minimising the lifetime and never storing it in component state
        // is the bound (the key-agent zeroises the derived bytes itself).
        login.privateKeyHex = '';
      }

      const result = await api.prove(vaultId, challenge.nonce_hex, sigHex);
      if (result.kind === 'ok') {
        setState({ kind: 'signed' });
        onSessionEstablished(result.value);
        return;
      }

      let message: string;
      switch (result.kind) {
        case 'invalid-signature':
          message =
            "This Web3Auth account doesn't control this vault. Sign in with the same login you created the vault with.";
          break;
        case 'rate-limited':
          message =
            'Too many attempts in a short window. Wait a minute and try again.';
          break;
        case 'expired-or-used-nonce':
          message =
            'The ownership challenge expired before we finished. Please try again.';
          break;
        default:
          message = result.message ?? 'Unknown error while proving ownership.';
      }
      setState({ kind: 'error', message });
    } finally {
      void web3authLogout();
    }
  }

  if (state.kind === 'signed') {
    return (
      <div class="recovery-card recovery-card--success">
        <h3 class="recovery-title">Ownership verified</h3>
        <p>Loading vault details…</p>
      </div>
    );
  }

  return (
    <div class="recovery-card">
      <h3 class="recovery-title">Sign in with Web3Auth</h3>

      <div class="recovery-warning" role="note">
        Your key is reconstructed in <strong>your browser</strong> from your
        Web3Auth login, used once to sign the ownership challenge, and then
        dropped. It is never sent to any server — only the derived signature
        leaves this page. The verifier session is signed out immediately after.
      </div>

      {state.kind === 'connecting' && (
        <div class="recovery-progress">Opening Web3Auth sign-in…</div>
      )}
      {state.kind === 'signing' && (
        <div class="recovery-progress">Signing and proving ownership…</div>
      )}

      {state.kind === 'error' && (
        <div class="recovery-error" role="alert">
          {state.message}
        </div>
      )}

      <div class="recovery-actions">
        <button
          type="button"
          class="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={handleSignIn}
          disabled={busy}
          data-testid="web3auth-sign"
        >
          {state.kind === 'connecting'
            ? 'Opening…'
            : state.kind === 'signing'
              ? 'Signing…'
              : 'Sign in with Web3Auth'}
        </button>
      </div>
    </div>
  );
}
